import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../axiosConfig';
import './ConversationsSection.css';

// Icons used in this component
const Icons = {
  Message: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
  ),
  ChevronLeft: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
};

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

function ConversationsSection({ conversations, onSelectConversation, selectedConversation, loading, sidebarWidth = 280 }) {
  const isMobile = useIsMobile();
  const [messageInput, setMessageInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.interaction_request_id);
      if (!isMobile) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [selectedConversation, isMobile]);

  const loadMessages = async (requestId) => {
    setMessagesLoading(true);
    try {
      const response = await axiosInstance.get(`club-interface/conversations/${requestId}/messages/`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
    setMessagesLoading(false);
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      await axiosInstance.post(
        `club-interface/conversations/${selectedConversation.interaction_request_id}/send/`,
        { content: messageInput }
      );
      setMessageInput("");
      loadMessages(selectedConversation.interaction_request_id);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
    setSending(false);
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));
    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    if (diffDays === 0) return time;
    if (diffDays === 1) return `Yesterday ${time}`;
    if (diffDays < 7) return `${date.toLocaleDateString([], { weekday: 'short' })} ${time}`;
    return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
  };

  const formatDateSeparator = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const formatConversationTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const shouldShowDateSeparator = (currentMsg, prevMsg) => {
    if (!prevMsg) return true;
    const currentDate = new Date(currentMsg.timestamp).toDateString();
    const prevDate = new Date(prevMsg.timestamp).toDateString();
    return currentDate !== prevDate;
  };

  const isGroupedMessage = (currentMsg, prevMsg) => {
    if (!prevMsg) return false;
    if (currentMsg.is_sent_by_club !== prevMsg.is_sent_by_club) return false;
    const timeDiff = new Date(currentMsg.timestamp) - new Date(prevMsg.timestamp);
    return timeDiff < 2 * 60 * 1000;
  };

  const renderMessages = () => {
    return messages.map((msg, index) => {
      const prevMsg = messages[index - 1];
      const nextMsg = messages[index + 1];
      const showDateSeparator = shouldShowDateSeparator(msg, prevMsg);
      const isGrouped = isGroupedMessage(msg, prevMsg);
      const isLastInGroup = !nextMsg || !isGroupedMessage(nextMsg, msg);

      return (
        <React.Fragment key={msg.id || index}>
          {showDateSeparator && (
            <div className="piq-msg__date-separator">
              <span>{formatDateSeparator(msg.timestamp)}</span>
            </div>
          )}
          <div className={`piq-msg__message ${msg.is_sent_by_club ? 'piq-msg__message--sent' : 'piq-msg__message--received'} ${isGrouped ? 'piq-msg__message--grouped' : ''} ${isLastInGroup ? 'piq-msg__message--last' : ''}`}>
            <div className="piq-msg__bubble">
              <p className="piq-msg__text">{msg.content}</p>
            </div>
            {isLastInGroup && (
              <span className="piq-msg__meta">{formatMessageTime(msg.timestamp)}</span>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  const sortedConversations = conversations
    ?.filter(c => c.status === 'approved')
    .sort((a, b) => {
      const aTime = a.latest_message?.timestamp ? new Date(a.latest_message.timestamp) : new Date(0);
      const bTime = b.latest_message?.timestamp ? new Date(b.latest_message.timestamp) : new Date(0);
      return bTime - aTime;
    }) || [];

  const showChatOnMobile = isMobile && selectedConversation;

  // The actual component content
  const content = (
    <div
      className={`piq-msg ${showChatOnMobile ? 'piq-msg--chat-open' : ''} ${isMobile ? 'piq-msg--mobile' : ''}`}
      style={{ '--sidebar-width': `${sidebarWidth}px` }}
    >
      {/* Header */}
      {!showChatOnMobile && (
        <div className="piq-msg__header">
          <div>
            <h1 className="piq-msg__title">Messages</h1>
            <p className="piq-msg__subtitle">
              {sortedConversations.length} {sortedConversations.length === 1 ? 'conversation' : 'conversations'}
            </p>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="piq-msg__container">
        {/* Conversation List */}
        <div className={`piq-msg__list ${showChatOnMobile ? 'piq-msg__list--hidden' : ''}`}>
          {loading ? (
            <div className="piq-msg__loading">
              <div className="piq-msg__spinner"></div>
            </div>
          ) : sortedConversations.length > 0 ? (
            sortedConversations.map((conv) => (
              <div
                key={conv.interaction_request_id}
                className={`piq-msg__item ${selectedConversation?.interaction_request_id === conv.interaction_request_id ? 'piq-msg__item--active' : ''} ${conv.unread_count > 0 ? 'piq-msg__item--unread' : ''}`}
                onClick={() => onSelectConversation(conv)}
              >
                <div className="piq-msg__avatar">
                  {(conv.user?.full_name || "U").charAt(0).toUpperCase()}
                </div>
                <div className="piq-msg__item-content">
                  <div className="piq-msg__item-header">
                    <h4 className="piq-msg__item-name">{conv.user?.full_name || "Unknown User"}</h4>
                    <span className="piq-msg__item-time">
                      {formatConversationTime(conv.latest_message?.timestamp)}
                    </span>
                  </div>
                  <div className="piq-msg__item-preview">
                    <p className="piq-msg__item-last">
                      {conv.latest_message?.content || "No messages yet"}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="piq-msg__badge">{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="piq-msg__empty">
              <div className="piq-msg__empty-icon">
                <Icons.Message />
              </div>
              <h3>No conversations yet</h3>
              <p>When members approve your connection requests, you can message them here.</p>
            </div>
          )}
        </div>

        {/* Chat Window */}
        <div className={`piq-msg__chat ${!selectedConversation ? 'piq-msg__chat--empty' : ''}`}>
          {selectedConversation ? (
            <>
              <div className="piq-msg__chat-header">
                {isMobile && (
                  <button
                    className="piq-msg__back-btn"
                    onClick={() => onSelectConversation(null)}
                    aria-label="Back to conversations"
                  >
                    <Icons.ChevronLeft />
                  </button>
                )}
                <div className="piq-msg__chat-user">
                  <div className="piq-msg__chat-avatar">
                    {(selectedConversation.user?.full_name || "U").charAt(0).toUpperCase()}
                  </div>
                  <div className="piq-msg__chat-details">
                    <h4>{selectedConversation.user?.full_name || "Unknown User"}</h4>
                    <span className="piq-msg__chat-meta">
                      {selectedConversation.user?.demographics?.age && `${selectedConversation.user.demographics.age} years old`}
                      {selectedConversation.user?.demographics?.current_club_member === "yes" && " • Club Member"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="piq-msg__messages">
                {messagesLoading ? (
                  <div className="piq-msg__loading">
                    <div className="piq-msg__spinner"></div>
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length > 0 ? (
                  <>
                    {renderMessages()}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="piq-msg__chat-empty">
                    <div className="piq-msg__chat-empty-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        <path d="M8 10h.01"></path>
                        <path d="M12 10h.01"></path>
                        <path d="M16 10h.01"></path>
                      </svg>
                    </div>
                    <h3>Start the conversation</h3>
                    <p>Say hello to {selectedConversation.user?.full_name?.split(' ')[0] || 'this member'}!</p>
                  </div>
                )}
              </div>

              <div className="piq-msg__input-area">
                <div className="piq-msg__input-wrapper">
                  <textarea
                    ref={inputRef}
                    className="piq-msg__input"
                    placeholder="Type your message..."
                    value={messageInput}
                    onChange={(e) => {
                      if (e.target.value.length <= 1000) {
                        setMessageInput(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    rows={1}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                  />
                  {messageInput.length > 800 && (
                    <span className={`piq-msg__char-count ${messageInput.length >= 1000 ? 'piq-msg__char-count--limit' : ''}`}>
                      {messageInput.length}/1000
                    </span>
                  )}
                </div>
                <button
                  className="piq-msg__send-btn"
                  onClick={sendMessage}
                  disabled={!messageInput.trim() || sending}
                  aria-label="Send message"
                >
                  {sending ? (
                    <div className="piq-msg__send-spinner"></div>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="piq-msg__no-selection">
              <div className="piq-msg__no-selection-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <h3>Select a conversation</h3>
              <p>Choose someone from the list to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // On mobile, render inline (no portal) so it flows naturally after subscription banner
  // On desktop, use Portal to render outside React tree for fixed positioning
  if (isMobile) {
    return content;
  }
  return ReactDOM.createPortal(content, document.body);
}

export default ConversationsSection;
